/**
 * Sample signed-in account.
 * Profile fields come from the session. Plan, coins, and device count stay here
 * until those APIs are ready. Each block already has a skeleton.
 */

export interface AccountSnapshot {
  premium: boolean;
  earlyAccess: boolean;
  validTill: string;
  coins: number;
  memberSince: string;
  deviceCount: number;
}

export const SAMPLE_ACCOUNT: AccountSnapshot = {
  premium: false,
  earlyAccess: false,
  validTill: '31 Dec 2026',
  coins: 250,
  memberSince: '12 Mar 2024',
  deviceCount: 2,
};

export const COINS_DESC = 'Use coins to access premium features.';
export const COINS_PLAY_DESC =
  'Each play session in stationary, playlist, or itinerary mode uses one mock coin.';
export const SYNCED_DESC = 'Your data is synced securely.';
export const EARN_DESC =
  'Watching an ad to earn coins is available in the MockLoc Android app. This browser shows the balance once coin sync is connected.';
export const PREMIUM_OFF_TITLE = 'Premium is not active';
export const PREMIUM_OFF_BODY =
  'Unlimited sessions do not spend mock coins. Early community access is granted from the MockLoc app.';
export const PREMIUM_OFF_DIALOG =
  'Premium checkout is not on the web yet. Request early community access from the MockLoc Android app. While Premium is active, sessions do not spend mock coins.';
export const PREMIUM_ON_DIALOG =
  'Early community access is active. Premium sessions do not spend mock coins.';
export const SIGNOUT_DESC =
  'Sign out of MockLoc on this browser? Saved locations stay on the account.';

export function isGoogleLogin(loginType: string): boolean {
  return loginType.trim().toUpperCase() === 'GOOGLE';
}

export function signInTitle(loginType: string): string {
  return isGoogleLogin(loginType) ? 'Signed in with Google' : 'Signed in with email';
}

export function signInShort(loginType: string): string {
  return isGoogleLogin(loginType) ? 'Logged in with Google' : 'Logged in with email';
}

export function accountInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'M';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

export function safeCount(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

export function deviceCountLabel(count: number): string {
  const safe = safeCount(count);
  if (safe === 0) return 'No devices signed in';
  if (safe === 1) return '1 device signed in';
  return `${safe} devices signed in`;
}
